/*
  ECSR-1321 / ECSR-1428 — keyboard focus for the Alia discount popup.

  The popup is injected by the Alia app embed, not by the theme: about three seconds
  after load Alia appends <div id="alia-root-…"> to <body>, with the popup's controls
  inside an open shadow root. The same host shows two things:

    - the full popup: a full-screen overlay that Alia marks role="dialog"
      aria-modal="true". Alia focuses the host, but nothing inside it, so the next
      Tab would leave the popup for the hidden page behind.
    - the minimized "Exclusive Discount" bar it shrinks to when the full popup is
      closed: a small corner button with no dialog role. It is part of the page, not
      a modal, and must never hold focus (ECSR-1428: holding it there closed the
      iPhone keyboard on every tap into search or any other field).

  This script only manages focus around the popup. It never changes the popup's
  content, styling, timing or form handling, and it does not handle Escape (Alia
  already closes on Escape). The only attribute it changes on Alia's host is the
  tabindex="-1" Alia keeps there, taken off while only the minimized bar shows (a
  negative tabindex on a shadow host hides its whole contents from Tab).

    - When the full popup opens (including from the minimized bar): remember where
      the shopper was, move focus to the first control in the popup, and make sure
      the host is a named dialog (only filling in role / name if Alia ever leaves
      them out, and taking them back off when it closes).
    - While it is open: Tab and Shift+Tab cycle through the popup's visible controls,
      and focus that lands outside the popup is brought back to it.
    - When it closes (Escape, close, "No, thanks", shrinking to the minimized bar,
      content hidden or removed): if focus was left inside the popup or on the page
      body, return it to where the shopper was before it opened.
    - The minimized bar is left alone: it is reached and passed with Tab like any
      other page control, and tapping or tabbing into the rest of the page is never
      pulled back to it.
*/
(function () {
  'use strict';

  var HOST_ID_PREFIX = 'alia-root-'; // the numeric suffix is the shop / flow id
  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');
  var SHADOW_WAIT_MS = 250; // retry interval while waiting for Alia to attach its shadow root
  var SHADOW_WAIT_TRIES = 40;
  var SETTLE_MS = 400; // re-check after open / close animations settle
  var COVER_RATIO = 0.9; // the full popup is a full-screen overlay; the minimized bar is not
  var BAR = {}; // marker: the shopper was on the minimized bar when the full popup opened

  var host = null;
  var hostObserver = null;
  var isOpen = false; // the full popup (the modal), never the minimized bar
  var returnTo = null; // where the shopper was when the full popup opened: an element, BAR or null
  var lastPlace = null; // where the shopper last was outside the full popup: an element, BAR or null
  var barPending = false; // closed back to the minimized bar before Alia had rendered it
  var tabindexTaken = false; // the host's tabindex="-1" is off while only the minimized bar shows
  var added = {}; // dialog attributes this script filled in, removed again on close
  var checkQueued = false;
  var initialised = false;
  var lastTabShift = false; // direction of the most recent Tab, for pulling focus back in
  var lastKeyEscape = false; // Alia closes on Escape and moves focus itself; don't fight it

  function isHost(el) {
    return !!el && el.nodeType === 1 && typeof el.id === 'string' && el.id.indexOf(HOST_ID_PREFIX) === 0;
  }

  function isVisible(el) {
    if (!el || !el.getClientRects().length) return false;
    var rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && window.getComputedStyle(el).visibility !== 'hidden';
  }

  // Once dismissed, Alia keeps the host as a small "Exclusive Discount" teaser button in
  // a corner of the page. That is not a modal and must not hold focus: the popup only
  // counts as open while the host covers the viewport, as the full popup does.
  function coversViewport() {
    var rect = host.getBoundingClientRect();
    return rect.width >= window.innerWidth * 0.9 && rect.height >= window.innerHeight * 0.9;
  }

  function getControls() {
    if (!host || !host.isConnected || !host.shadowRoot || !coversViewport()) return [];
    return Array.prototype.filter.call(host.shadowRoot.querySelectorAll(FOCUSABLE), isVisible);
  }

  // The full popup, as opposed to the minimized bar: Alia marks it a modal dialog,
  // and it is laid out as an overlay covering the viewport. The bar is neither.
  function isFullPopup() {
    if (host.getAttribute('aria-modal') === 'true' && !added['aria-modal']) return true;
    var rect = host.getBoundingClientRect();
    var vw = window.innerWidth || document.documentElement.clientWidth;
    var vh = window.innerHeight || document.documentElement.clientHeight;
    return rect.width >= vw * COVER_RATIO && rect.height >= vh * COVER_RATIO;
  }

  // Focus is "lost" when it sits on the page body, or on the popup host / inside the popup.
  function focusIsLostOrInPopup() {
    var active = document.activeElement;
    return !active || active === document.body || active === document.documentElement || active === host;
  }

  // Only fill in what Alia leaves out; never overwrite its own role or name.
  function fillIn(name, value) {
    host.setAttribute(name, value);
    added[name] = true;
  }

  function ensureNamedDialog() {
    if (!host.getAttribute('role')) fillIn('role', 'dialog');
    if (!host.hasAttribute('aria-modal')) fillIn('aria-modal', 'true');
    var label = window.theme && theme.strings && theme.strings.promotionalPopup;
    if (label && !host.getAttribute('aria-label') && !host.getAttribute('aria-labelledby')) {
      fillIn('aria-label', label);
    }
  }

  // Take back what ensureNamedDialog filled in, so the minimized bar is never
  // announced as a modal dialog that hides the rest of the page.
  function removeFilledIn() {
    for (var name in added) {
      if (Object.prototype.hasOwnProperty.call(added, name) && host) host.removeAttribute(name);
    }
    added = {};
  }

  // Put focus on the first popup control, unless the shopper is already in one.
  // `onOpening` moves focus in from wherever it is; after a re-render it is only
  // pulled back in if it was dropped.
  function focusPopup(controls, onOpening) {
    if (host.shadowRoot && host.shadowRoot.activeElement) return;
    if (!onOpening && !focusIsLostOrInPopup()) return;
    if (controls[0]) {
      controls[0].focus();
    } else if (host.hasAttribute('tabindex')) {
      host.focus();
    }
  }

  function onOpen(controls) {
    isOpen = true;
    var active = document.activeElement;
    // Alia has usually focused its host by now, so fall back to the last place seen.
    returnTo = active && active !== host && active !== document.body && active !== document.documentElement ? active : lastPlace;
    ensureNamedDialog();
    focusPopup(controls, true);
  }

  function onClose(controls) {
    isOpen = false;
    removeFilledIn();
    var target = returnTo;
    returnTo = null;
    // Only step in if focus was left behind in the popup or on the page body; if the
    // shopper has already moved on, leave them there.
    if (!focusIsLostOrInPopup()) return;
    if (target === BAR) {
      // The full popup was opened from the minimized bar: go back to the bar, as
      // soon as Alia has rendered it.
      if (controls[0]) {
        controls[0].focus();
      } else {
        barPending = true;
      }
      return;
    } else if (target && target.isConnected) {
      target.focus({ preventScroll: true });
    }
    // Nowhere to go back to (the popup opened by itself before the shopper focused
    // anything): don't leave focus parked on the host or the minimized bar, where
    // only Alia or this script put it; return it to the page.
    if (host && document.activeElement === host) {
      var inner = host.shadowRoot && host.shadowRoot.activeElement;
      if (inner && inner.blur) inner.blur();
      if (document.activeElement === host) host.blur();
    }
  }

  // Alia keeps tabindex="-1" on its host, and a host with a negative tabindex takes
  // everything inside it out of the Tab order, so the minimized bar could only be
  // reached with a mouse or finger. While only the bar shows, take that tabindex off
  // so the bar's button is a normal Tab stop; put it back when the full popup opens,
  // where Alia focuses the host itself.
  function syncTabOrder(full, controls) {
    if (!full && controls.length && host.getAttribute('tabindex') === '-1') {
      host.removeAttribute('tabindex');
      tabindexTaken = true;
    } else if (full && tabindexTaken) {
      host.setAttribute('tabindex', '-1');
      tabindexTaken = false;
    }
  }

  function check() {
    checkQueued = false;
    var controls = getControls();
    var full = controls.length > 0 && isFullPopup();
    syncTabOrder(full, controls);
    if (barPending && !full && controls.length) {
      barPending = false;
      if (focusIsLostOrInPopup()) controls[0].focus();
    }
    if (full && !isOpen) {
      barPending = false;
      onOpen(controls);
    } else if (full && isOpen) {
      // Alia re-rendered a step (e.g. the form): pull focus back in if it was dropped.
      focusPopup(controls);
    } else if (!full && isOpen) {
      // Closed, or shrunk to the minimized bar.
      onClose(controls);
    }
  }

  function queueCheck() {
    if (checkQueued) return;
    checkQueued = true;
    window.requestAnimationFrame(check);
    window.setTimeout(check, SETTLE_MS);
  }

  function watchHost(el, tries) {
    if (el === host && hostObserver) return;
    if (!el.shadowRoot) {
      // Alia may attach the shadow root just after appending the host.
      if (tries < SHADOW_WAIT_TRIES) {
        window.setTimeout(function () {
          if (el.isConnected) watchHost(el, tries + 1);
        }, SHADOW_WAIT_MS);
      }
      return;
    }
    if (hostObserver) hostObserver.disconnect();
    host = el;
    added = {};
    tabindexTaken = false;
    hostObserver = new MutationObserver(queueCheck);
    var opts = { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'hidden'] };
    hostObserver.observe(host.shadowRoot, opts);
    hostObserver.observe(host, { attributes: true, attributeFilter: ['style', 'class', 'hidden', 'aria-modal', 'tabindex'] });
    queueCheck();
  }

  function scanBody() {
    var children = document.body ? document.body.children : [];
    for (var i = 0; i < children.length; i++) {
      if (isHost(children[i])) {
        watchHost(children[i], 0);
        return;
      }
    }
    // The host was removed while open.
    if (host && !host.isConnected) queueCheck();
  }

  // Keep Tab / Shift+Tab inside the open full popup.
  function onKeydown(e) {
    lastKeyEscape = e.key === 'Escape' || e.key === 'Esc';
    if (e.key === 'Tab') lastTabShift = e.shiftKey;
    if (!isOpen || e.key !== 'Tab' || e.defaultPrevented) return;
    var controls = getControls();
    if (!controls.length) return;
    var first = controls[0];
    var last = controls[controls.length - 1];
    var active = host.shadowRoot.activeElement;

    if (!active || controls.indexOf(active) === -1) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
    } else if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // Remember the shopper's place on the page. Events from inside the popup are
  // retargeted to the host: a control focused there while only the minimized bar is
  // showing means the shopper is on the bar; Alia focusing the host itself is not a
  // place. While the full popup is open it is the topmost modal: if focus lands
  // outside it (e.g. a page widget takes the first Tab and cancels it), pull focus
  // back to the first control (last on Shift+Tab). The minimized bar never does this.
  function onFocusin(e) {
    var target = e.target;
    if (isHost(target) || target === host) {
      if (!isOpen && target.shadowRoot && target.shadowRoot.activeElement) lastPlace = BAR;
      return;
    }
    if (isOpen && host && host.isConnected && !lastKeyEscape) {
      var controls = getControls();
      if (controls.length) {
        (lastTabShift ? controls[controls.length - 1] : controls[0]).focus();
        return;
      }
    }
    barPending = false; // the shopper has moved on
    if (target !== document.body) lastPlace = target;
  }

  // Focus leaving a page control for nothing (a tap on empty page) means the
  // shopper is no longer anywhere in particular.
  function onFocusout(e) {
    if (isOpen || e.relatedTarget || isHost(e.target)) return;
    lastPlace = null;
  }

  function init() {
    if (initialised || !document.body || !window.MutationObserver) return;
    initialised = true;
    var on = window.on ? window.on.bind(window) : function (event, cb, opts) {
      window.addEventListener(event.split('.')[0], cb, opts);
    };
    on('keydown.aliaPopupA11y', onKeydown, true);
    on('focusin.aliaPopupA11y', onFocusin, true);
    on('focusout.aliaPopupA11y', onFocusout, true);
    new MutationObserver(scanBody).observe(document.body, { childList: true });
    scanBody();
  }

  // theme.js fires page:loaded before this deferred script runs, so start as soon as
  // the DOM is ready; the page:loaded listener stays as a harmless fallback (init is
  // idempotent).
  if (document.readyState !== 'loading' && document.body) {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
  document.addEventListener('page:loaded', init);
})();
